import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../api/client';
import type { AttentionRow, DashboardTotals, SyncOverview, SyncedLicenseRow } from '../api/types';
import { IconCheck, IconChevronRight, IconRefresh } from '../components/icons';
import { Pill } from '../components/license';
import { Button, Callout, EmptyState, ErrorNote, SkeletonRows, useToast } from '../components/ui';
import { EXPIRING_WINDOW_DAYS, formatDate, formatDays, formatRelative } from '../lib/license';

/**
 * The overview leads with what needs attention, not with counts.
 *
 * A staff member signing in has a job to do; a wall of metric tiles is a
 * decorative answer to a question nobody asked. The estate summary is a
 * compact strip underneath, deliberately subordinate.
 */
export function DashboardPage() {
  const { data, isPending, isError, error } = useQuery({
    queryKey: ['dashboard'],
    queryFn: () =>
      api.get<{ totals: DashboardTotals; attention: AttentionRow[] }>(
        '/api/staff/licenses/summary/dashboard',
      ),
  });

  const attention = data?.attention ?? [];
  const expired = attention.filter((row) => row.is_expired);
  const soon = attention.filter((row) => !row.is_expired);

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Overview</h1>
          <p className="page-head__sub">
            Licenses that have run out, or are about to. Everything else is working.
          </p>
        </div>
      </div>

      <section className="panel">
        <div className="panel__head">
          <div>
            <h2 className="panel__title">Needs attention</h2>
            <p className="panel__hint">
              An expired license blocks every user at that company, including their administrator.
            </p>
          </div>
          {!isPending && attention.length > 0 && (
            <Pill tone={expired.length > 0 ? 'expired' : 'expiring'}>
              {expired.length > 0
                ? `${expired.length} blocking access`
                : `${soon.length} within 30 days`}
            </Pill>
          )}
        </div>

        {isPending && <SkeletonRows rows={4} />}

        {isError && (
          <div className="panel__body">
            <p className="danger-text">{(error as Error).message}</p>
          </div>
        )}

        {!isPending && !isError && attention.length === 0 && (
          <EmptyState
            icon={<IconCheck size={30} />}
            title="Nothing expiring"
            body="No license has run out, and none expires within the next 30 days. Companies and licenses are managed from the sections on the left."
          />
        )}

        {attention.length > 0 && (
          <div className="attention">
            {attention.map((row) => (
              <Link
                key={row.id}
                to={`/licenses?focus=${row.id}`}
                className="attention__row"
              >
                <span className="attention__company">{row.company_name}</span>
                <span className="attention__product">{row.product_name}</span>
                <span className="cell-stack">
                  <Pill tone={row.is_expired ? 'expired' : 'expiring'}>
                    {row.is_expired
                      ? 'Access blocked'
                      : `${formatDays(row.days_remaining)} left`}
                  </Pill>
                  <span className="cell-meta tabular">
                    {row.is_expired ? 'expired' : 'expires'} {formatDate(row.expires_at)}
                  </span>
                </span>
                <IconChevronRight className="faint" />
              </Link>
            ))}
          </div>
        )}
      </section>

      <SyncPanel />

      {data && (
        <section className="panel">
          <div className="panel__head">
            <h2 className="panel__title">The estate</h2>
          </div>
          <div className="summary">
            <SummaryItem label="Companies" value={data.totals.active_companies} />
            <SummaryItem label="Products" value={data.totals.active_products} />
            <SummaryItem label="Credentials" value={data.totals.active_users} />
            <SummaryItem label="Working licenses" value={data.totals.usable_licenses} />
            <SummaryItem
              label="Expiring within 30 days"
              value={data.totals.expiring_soon}
              tone={data.totals.expiring_soon > 0 ? 'warn' : undefined}
            />
            <SummaryItem
              label="Expired"
              value={data.totals.expired_licenses}
              tone={data.totals.expired_licenses > 0 ? 'alert' : undefined}
            />
          </div>
        </section>
      )}
    </>
  );
}

function SummaryItem({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone?: 'warn' | 'alert';
}) {
  return (
    <div className={`summary__item${tone ? ` summary__item--${tone}` : ''}`}>
      <span className="summary__value">{value}</span>
      <span className="summary__label">{label}</span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Synced from other systems
// ---------------------------------------------------------------------------

/**
 * Every license Heron copies in from another system (Sources page), one row
 * per company and product, worst first. Expiry is read from license_overview —
 * the same view the login gate uses — so what this says is exactly what a
 * sign-in will be judged against.
 */
function SyncPanel() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { push } = useToast();

  const { data, isPending, isError, error } = useQuery({
    queryKey: ['sync-overview'],
    queryFn: () => api.get<SyncOverview>('/api/staff/sync-sources/overview'),
    refetchInterval: 60_000,
  });

  const syncAll = useMutation({
    mutationFn: async () => {
      const results = await Promise.all(
        (data?.sources ?? [])
          .filter((s) => s.is_enabled)
          .map((s) => api.post<{ ok: boolean }>(`/api/staff/sync-sources/${s.id}/run`)),
      );
      return results.every((r) => r.ok);
    },
    onSuccess: (allOk) => {
      for (const key of ['sync-overview', 'sync-sources', 'dashboard', 'companies']) {
        void queryClient.invalidateQueries({ queryKey: [key] });
      }
      push(
        allOk
          ? { tone: 'success', title: 'All sources synced' }
          : { tone: 'error', title: 'A source did not sync — see Sources' },
      );
    },
  });

  // No sources configured: nothing to say on the overview.
  if (!isPending && !isError && data && data.sources.length === 0) return null;

  const rows = data?.licenses ?? [];
  const failing = (data?.sources ?? []).filter((s) => s.last_run_ok === false);
  const skipped = (data?.sources ?? []).reduce((n, s) => n + (s.skipped_count ?? 0), 0);

  return (
    <section className="panel">
      <div className="panel__head">
        <div>
          <h2 className="panel__title">Synced from other systems</h2>
          <p className="panel__hint">
            {data
              ? data.sources
                  .map((s) =>
                    `${s.name}: ${s.last_success_at ? `synced ${formatRelative(s.last_success_at)}` : 'not synced yet'}`,
                  )
                  .join(' · ')
              : 'Companies, expiry dates and logins copied in from other systems.'}
          </p>
        </div>
        {data && (
          <div className="sync-panel__actions">
            <Link to="/sources" className="small">
              Sources
            </Link>
            <Button size="sm" icon={<IconRefresh />} loading={syncAll.isPending} onClick={() => syncAll.mutate()}>
              Sync now
            </Button>
          </div>
        )}
      </div>

      {isPending && <SkeletonRows rows={3} />}
      {isError && (
        <div className="panel__body">
          <ErrorNote error={error} />
        </div>
      )}

      {(failing.length > 0 || skipped > 0 || syncAll.isError) && (
        <div className="panel__body stack">
          {syncAll.isError && <ErrorNote error={syncAll.error} />}
          {failing.map((s) => (
            <Callout key={s.id} tone="danger" title={`${s.name}: last sync failed ${formatRelative(s.last_run_at)}`}>
              {s.last_run_error} The rows below are from its last sync that worked.
            </Callout>
          ))}
          {skipped > 0 && (
            <Callout tone="warn" title={`${skipped} record(s) not copied in`}>
              See <Link to="/sources">Sources</Link> for which, and why.
            </Callout>
          )}
        </div>
      )}

      {data && rows.length === 0 && failing.length === 0 && (
        <EmptyState
          icon={<IconCheck size={30} />}
          title="Nothing synced yet"
          body="Companies from your sources appear here after their first sync."
        />
      )}

      {rows.length > 0 && (
        <div className="table-wrap scroll-area">
          <table className="table">
            <thead>
              <tr>
                <th>Company</th>
                <th>Product</th>
                <th>Status</th>
                <th>Expires</th>
                <th className="table__num">Logins</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr
                  key={row.license_id}
                  data-clickable="true"
                  onClick={() => navigate(`/companies/${row.company_id}`)}
                >
                  <td>
                    <span className="cell-strong">{row.company_name}</span>
                  </td>
                  <td>
                    <span className="cell-stack">
                      <span>{row.product_name}</span>
                      <span className="cell-meta">from {row.source_name}</span>
                    </span>
                  </td>
                  <td>
                    <SyncStatus row={row} />
                  </td>
                  <td>
                    <span className="cell-stack">
                      <span className="tabular sync-panel__date">
                        {row.never_expires ? 'No expiry' : formatDate(row.expires_at)}
                      </span>
                      {!row.never_expires && (
                        <span className="cell-meta">
                          {row.is_expired ? 'expired' : `${formatDays(row.days_remaining)} left`}
                        </span>
                      )}
                    </span>
                  </td>
                  <td className="table__num">{row.login_count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function SyncStatus({ row }: { row: SyncedLicenseRow }) {
  if (row.status === 'suspended') return <Pill tone="suspended">Suspended</Pill>;
  if (row.is_expired) return <Pill tone="expired">Expired</Pill>;
  if (!row.is_usable) return <Pill tone="pending">Not started</Pill>;
  if (!row.never_expires && row.days_remaining <= EXPIRING_WINDOW_DAYS) {
    return <Pill tone="expiring">Expiring</Pill>;
  }
  return <Pill tone="live">Active</Pill>;
}
